import { useCallback } from 'react';

import { Orders } from '@/api/Orders';
import {
  ChannelCommentsResponse,
  CommentChannelQuery,
  CommentMessageRequest,
  CommentRequestDataWithFiles,
  CommentResponse,
  LocationCommentsResponse,
  PageResponseUserInfo,
  UsersRequest,
} from '@/api/data-contracts';
import { ContentType } from '@/api/http-client';
import { optionalApiMethod } from '@/shared/api';

export const useOrderCommentMethods = (ordersApi: Orders) => {
  const getOrderComments = useCallback(
    async (
      orderId: string,
      channel?: string,
      locationId?: string,
      signal?: AbortSignal | undefined,
    ): Promise<LocationCommentsResponse[]> => {
      const { data } = await ordersApi.getOrderComments(
        orderId,
        { channel, locationId },
        { signal },
      );
      return data;
    },
    [ordersApi],
  );

  /**
   * One entry per requested channel. Backends that do not serve the batch route yet are answered
   * from the per-channel endpoint — the same calls these counts were built from before the batch
   * route existed — so the shape callers see is identical either way.
   */
  const getOrderCommentsBatch = useCallback(
    async (
      orderId: string,
      channels: CommentChannelQuery[],
      signal?: AbortSignal | undefined,
    ): Promise<ChannelCommentsResponse[]> => {
      const fetchBatch = optionalApiMethod<
        (
          orderId: string,
          request: { channels: CommentChannelQuery[] },
          params: { signal?: AbortSignal },
        ) => Promise<{ data: ChannelCommentsResponse[] }>
      >(ordersApi, 'getOrderCommentsBatch');

      if (fetchBatch) {
        const { data } = await fetchBatch(orderId, { channels }, { signal });
        return data;
      }

      // Settled, not all: the caller wraps this in one try/catch that zeroes EVERY channel, so a
      // single failing channel would blank the rest — which the per-channel fetching this stands
      // in for never did.
      const settled = await Promise.allSettled(
        channels.map(({ channel, locationId }) =>
          getOrderComments(orderId, channel, locationId, signal),
        ),
      );

      return channels.map(({ channel, locationId }, index) => {
        const result = settled[index];
        return {
          channel,
          locationId,
          locations: result.status === 'fulfilled' ? result.value : [],
        };
      });
    },
    [ordersApi, getOrderComments],
  );

  const getServicesOrderComments = useCallback(
    async (
      orderId: string,
      channel?: string,
      signal?: AbortSignal | undefined,
    ): Promise<LocationCommentsResponse[]> => {
      const query = channel ? `?channel=${encodeURIComponent(channel.toUpperCase())}` : '';
      const { data } = await ordersApi.request<LocationCommentsResponse[]>({
        path: `/services/orders/${orderId}/comments${query}`,
        method: 'GET',
        secure: true,
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const addComment = useCallback(
    async (orderId: string, request: CommentRequestDataWithFiles): Promise<CommentResponse> => {
      const { data } = await ordersApi.addOrderComment(orderId, request);
      return data;
    },
    [ordersApi],
  );

  const addServicesOrderComment = useCallback(
    async (
      orderId: string,
      locationId: string,
      request: CommentRequestDataWithFiles,
    ): Promise<CommentResponse> => {
      const { data } = await ordersApi.request<CommentResponse>({
        path: `/services/orders/${orderId}/${locationId}/comments`,
        method: 'POST',
        body: request,
        secure: true,
        type: ContentType.FormData,
      });
      return data;
    },
    [ordersApi],
  );

  const getOrderUsersForComments = useCallback(
    async (
      orderId: string,
      locationId: string,
      request: UsersRequest,
      signal?: AbortSignal | undefined,
    ): Promise<PageResponseUserInfo> => {
      const { data } = await ordersApi.getOrderUsersForComments(orderId, locationId, request, {
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const updateComment = useCallback(
    async (commentId: string, request: CommentMessageRequest): Promise<CommentResponse> => {
      const { data } = await ordersApi.updateOrderComment(commentId, request);
      return data;
    },
    [ordersApi],
  );

  const deleteComment = useCallback(
    async (commentId: string): Promise<void> => {
      await ordersApi.deleteOrderComment(commentId);
    },
    [ordersApi],
  );

  const downloadOrderCommentDocument = useCallback(
    async (resourceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await ordersApi.request<Blob>({
        path: `/orders/comments/${resourceId}/documents/${documentId}`,
        method: 'GET',
        secure: true,
        format: 'blob',
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const downloadServicesOrderCommentDocument = useCallback(
    async (resourceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await ordersApi.request<Blob>({
        path: `/services/orders/comments/${resourceId}/documents/${documentId}`,
        method: 'GET',
        secure: true,
        format: 'blob',
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  return {
    getOrderComments,
    getOrderCommentsBatch,
    getServicesOrderComments,
    addComment,
    addServicesOrderComment,
    getOrderUsersForComments,
    updateComment,
    deleteComment,
    downloadOrderCommentDocument,
    downloadServicesOrderCommentDocument,
  };
};
